"use client";

import React from "react";
import Link from "next/link";
import { Scale, Briefcase, FileSearch, ArrowRight, ShieldCheck } from "lucide-react";

interface LegalTopBarProps {
  activeTab: "portal" | "crm";
  currency: "INR" | "USD";
  onCurrencyToggle: (curr: "INR" | "USD") => void;
  onOpenCnrModal?: () => void;
  onOpenEvaluationModal?: () => void;
}

export default function LegalTopBar({
  activeTab,
  currency,
  onCurrencyToggle,
  onOpenCnrModal,
  onOpenEvaluationModal,
}: LegalTopBarProps) {
  return (
    <header className="sticky top-0 z-40 w-full bg-slate-950/90 backdrop-blur-md border-b border-slate-800">
      {/* Sandbox Regulatory Notice Banner */}
      <div className="bg-gradient-to-r from-slate-900 via-slate-850 to-slate-900 border-b border-amber-500/20 px-4 py-1.5 text-center text-[10px] sm:text-[11px] text-amber-400 flex items-center justify-center gap-2">
        <ShieldCheck className="w-3.5 h-3.5 shrink-0" />
        <span>
          <strong>BCI Rule 36 Compliant Demo Sandbox:</strong> Informational directory & legal workflow demonstration. No advertising or real privileged filing.
        </span>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
        {/* Chambers Logo & Brand */}
        <Link href="/demos/legal" className="flex items-center gap-3 group shrink-0">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-500/20 to-amber-600/10 border border-amber-500/40 flex items-center justify-center text-amber-400 group-hover:scale-105 transition-transform shadow-inner">
            <Scale className="w-5 h-5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-base sm:text-lg font-serif font-bold text-white tracking-wide">
                LexJuris
              </span>
              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/30 tracking-wider">
                CHAMBERS
              </span>
            </div>
            <p className="text-[10px] text-slate-400 hidden sm:block tracking-wide">
              Associates & Corporate Counsels • New Delhi
            </p>
          </div>
        </Link>

        {/* Navigation Actions */}
        <div className="flex items-center gap-2 sm:gap-4">
          {/* Currency Toggle */}
          <div className="flex items-center bg-slate-900 p-1 rounded-xl border border-slate-800 text-xs">
            <button
              type="button"
              onClick={() => onCurrencyToggle("INR")}
              className={`px-2.5 py-1 rounded-lg font-bold transition-all text-[11px] ${
                currency === "INR"
                  ? "bg-amber-500 text-slate-950 shadow-sm"
                  : "text-slate-400 hover:text-white"
              }`}
            >
              🇮🇳 INR (₹)
            </button>
            <button
              type="button"
              onClick={() => onCurrencyToggle("USD")}
              className={`px-2.5 py-1 rounded-lg font-bold transition-all text-[11px] ${
                currency === "USD"
                  ? "bg-amber-500 text-slate-950 shadow-sm"
                  : "text-slate-400 hover:text-white"
              }`}
            >
              🇺🇸 USD ($)
            </button>
          </div>

          {/* CNR Case Tracking Modal Trigger (Public Site) */}
          {activeTab === "portal" && onOpenCnrModal && (
            <button
              type="button"
              onClick={onOpenCnrModal}
              className="hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-900 hover:bg-slate-850 border border-slate-800 text-xs font-semibold text-slate-300 hover:text-white transition-colors"
            >
              <FileSearch className="w-3.5 h-3.5 text-amber-400" />
              <span>CNR Case Tracker</span>
            </button>
          )}

          {/* Evaluation Intake Trigger (Public Site) */}
          {activeTab === "portal" && onOpenEvaluationModal && (
            <button
              type="button"
              onClick={onOpenEvaluationModal}
              className="hidden lg:flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-amber-500/10 hover:bg-amber-500/20 border border-amber-500/30 text-xs font-bold text-amber-400 transition-colors"
            >
              <span>Preliminary Evaluation</span>
            </button>
          )}

          {/* Portal Switcher Tab: Website vs CRM */}
          {activeTab === "portal" ? (
            <Link
              href="/demos/legal/crm"
              className="flex items-center gap-2 px-3.5 sm:px-4 py-2 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 text-xs font-bold shadow-lg transition-all"
            >
              <Briefcase className="w-4 h-4" />
              <span className="hidden sm:inline">Attorney CRM Workspace</span>
              <span className="sm:hidden">CRM</span>
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          ) : (
            <Link
              href="/demos/legal"
              className="flex items-center gap-2 px-3.5 sm:px-4 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 border border-slate-700 text-slate-200 text-xs font-bold transition-all"
            >
              <Scale className="w-4 h-4 text-amber-400" />
              <span className="hidden sm:inline">Law Firm Website</span>
              <span className="sm:hidden">Portal</span>
            </Link>
          )}
        </div>
      </div>
    </header>
  );
}
